"use client";

import React from "react";
import SectionHeading from "./section-heading";
import { useSectionInView } from "@/lib/hooks";
import { motion } from "framer-motion";
import { useI18nContext } from "@/context/i18n-context";
import en from "@/locale/en";

type SkillItem = (typeof en.skills.data)[number];

// Group skills by category for better visual organization
const CATEGORY_ORDER = [
  "ML Frameworks",
  "Programming Languages",
  "Deployment",
  "Web Development",
  "Tools",
];

const SKILL_CATEGORIES: Record<string, string[]> = {
  "ML Frameworks": ["PyTorch", "TensorFlow", "Keras", "ONNX", "TensorRT", "OpenCV"],
  "Programming Languages": ["Python", "C++"],
  "Deployment": ["Docker", "Kubernetes"],
  "Web Development": ["TypeScript", "React", "Next.js", "Tailwind"],
  "Tools": ["Git", "PostgreSQL"],
};

function getCategoryForSkill(skillName: string): string {
  for (const [cat, skills] of Object.entries(SKILL_CATEGORIES)) {
    if (skills.includes(skillName)) return cat;
  }
  return "Other";
}

export default function Skills() {
  const { t } = useI18nContext();
  const links: { name: string; hash: string }[] = t("links");
  const { ref } = useSectionInView(links[3]?.name ?? "Skills", 0.5);

  const allSkills: SkillItem[] = t("skills.data");

  // Group skills
  const grouped: Record<string, SkillItem[]> = {};
  allSkills.forEach((item) => {
    const cat = getCategoryForSkill(item.skill);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  const orderedCategories = CATEGORY_ORDER.filter((c) => grouped[c]);

  return (
    <section
      id="skills"
      ref={ref}
      className="w-full max-w-[52rem] mb-24 sm:mb-32 scroll-mt-28"
    >
      <SectionHeading>{t("skills.title")}</SectionHeading>

      <div className="space-y-8">
        {orderedCategories.map((category, catIndex) => (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: 0.55,
              delay: catIndex * 0.07,
              ease: [0.25, 1, 0.5, 1],
            }}
          >
            <h3 className="text-xs font-medium tracking-[0.15em] uppercase
              text-[var(--color-text-tertiary)] mb-3">
              {category}
            </h3>
            <ul className="flex flex-wrap gap-2">
              {grouped[category].map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.35,
                    delay: catIndex * 0.07 + i * 0.04,
                    ease: [0.25, 1, 0.5, 1],
                  }}
                  className="flex items-center gap-2
                    bg-[var(--color-surface)]
                    border border-[var(--color-border-subtle)]
                    rounded-lg px-3.5 py-2
                    text-sm font-medium
                    text-[var(--color-text-secondary)]
                    hover:border-[var(--color-border)]
                    hover:text-[var(--color-text-primary)]
                    transition-colors duration-150"
                >
                  <span
                    style={{ color: item.color }}
                    className="text-base leading-none shrink-0"
                    aria-hidden="true"
                  >
                    {item.icon}
                  </span>
                  {item.skill}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
